import React, { useCallback, useEffect, useState } from "react";
import SummaryCard from "./SummaryCard";
import DealPayOffs from "./DealPayoffs";
import Offers from "./offers";
import FeesHoldbacks from "./FeesHoldbacks";
import Stipulations from "./Stipulations";
import Commission from "./Commission";
import Syndication from "./Syndication";
import FullPageLoader from "components/Loader/FullPage";
import { useStoreActions, useStoreState } from "easy-peasy";
import ExternalPayOffs from "./ExternalPayOffs";
import EarlyPayOffs from "./EarlyPayOffs";
import { get } from "requests";
import StorageUtils from "utils/StorageUtils";

interface Transaction {
  id: string;
  amount: number;
  description: string;
  txnDate: string;
  type: "credit" | "debit";
}

interface StatementWithTransactions {
  statementId: string;
  accountNumber: string;
  monthYear: string;
  source: string;
  transactions: Transaction[];
}

interface UnderwritingProps {
  dealApplicationId: number;
  isReadOnly: boolean;
  onViewTransactions?: (account: { accountnumber: string; monthyear: string }) => void;
  statementsForSummary: StatementWithTransactions[];
  permittedTabs: any
}

const Underwriting = ({ dealApplicationId, isReadOnly, permittedTabs }: UnderwritingProps) => {
  const isLoading = useStoreState((state: any) => state.offerStore.isLoading);
  const [isRefi, setIsRefi] = useState<boolean>(false);
  const offerDetails = useStoreState((state: any) => state.offerStore.contractTerms);
  const isAnalysisLoaded = useStoreState((state: any) => state.dealApplication.isAnalysisLoaded);
  const [source, setSource] = useState("");

  const advancesData = useStoreState((state: any) => state.bankSummaryStore.advancesData);
  const setAdvancesData = useStoreActions(
    (actions: any) => actions.bankSummaryStore.setAdvancesData
  );
  const bankStatementsSummary = useStoreState(
    (state: any) => state.bankSummaryStore.bankStatementData
  );
  const setBankStatementsSummary = useStoreActions(
    (actions: any) => actions.bankSummaryStore.setBankStatementData
  );

  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({
    offers: false,
    dealPayoffs: false,
    externalPayoffs: false,
    earlyPayoffs: false,
    feesHoldbacks: false,
    stipulations: false,
    commission: false,
    syndication: false,
  });

  const tabLabels = [
    ...(StorageUtils.isFeatureEnabled("smartScrub") ? ["SmartScrub"] : []),
    ...(StorageUtils.isFeatureEnabled("lendPathway") ? ["Lend Pathway"] : []),
    ...(StorageUtils.isFeatureEnabled("moneyThumb") ? ["MoneyThumb"] : []),
    ...(StorageUtils.isFeatureEnabled("ocrolus") ? ["Ocrolus"] : []),
    ...(StorageUtils.isFeatureEnabled("hyperVerge") ? ["HyperVerge"] : []),
  ];


  const setComponentLoading = useCallback((componentName: string, isLoading: boolean) => {
    setLoadingStates((prev) => ({
      ...prev,
      [componentName]: isLoading,
    }));
  }, []);

  const isGlobalLoading =
    Object.entries(loadingStates)
      .filter(([key]) => !["summaryCard", "bankStatements", "advancesPositions"].includes(key))
      .some(([_, state]) => state) || isLoading;

  
    useEffect(() => {
      if (tabLabels.length > 0) {
        const firstSource = tabLabels[0].toLowerCase();
        setSource(firstSource);
        }
    }, [tabLabels]);

   useEffect(() => {
      if(!isAnalysisLoaded && source){
            reloadSourceData(source);     
         }
    }, [source]);

  
  useEffect(() => {
    if (
      offerDetails?.contractTerms?.payingOffDeals &&
      offerDetails?.contractTerms?.payingOffDeals?.length > 0
    ) {
      setIsRefi(true);
    
    }
  }, [offerDetails]);

 const reloadSourceData =(selectedSource: any)=>{
   fetchBankStatementSummaryData(selectedSource);
   fetchAdvancesData(selectedSource);
 }
  
  const fetchBankStatementSummaryData = async (source: string) => {
   
    const s = (source || "").toLowerCase();
    if (s === "moneythumb" || s === "lend pathway" || s=== 'ocrolus') {
      setBankStatementsSummary([]);
      return;
    }
    
    try {
      setComponentLoading("bankStatements", true);
      const response = await get(`/ocr/transactionSummary/${dealApplicationId}`);
  
      let filteredData = response || [];

      const normalizedSource = (source || "").toLowerCase();
  
      if (normalizedSource === "smartscrub") {
        const allowedSources = ["ocr", "plaid", "internal"];
        filteredData = filteredData.filter((row: any) =>
          allowedSources.includes((row.source || "").toLowerCase())
        );
      } else if (normalizedSource === "moneythumb") {
        filteredData = filteredData.filter(
          (row: any) => (row.source || "").toLowerCase() === "moneythumb"
        );
      }
  
      const dataWithSelection =
        filteredData?.map((row: any) => ({
          ...row,
          selected: true,
        })) || [];
  
      setBankStatementsSummary(dataWithSelection);
    } catch {
      setBankStatementsSummary([]);
    } finally {
      setComponentLoading("bankStatements", false);
    }
  };
  
  const fetchAdvancesData = async (source: string) => {
    const s = (source || "").toLowerCase();

    if (s === "moneythumb" || s === "lend pathway"  || s=== 'ocrolus') {
      setAdvancesData([]);
      return;
    }
    
    try {
      setComponentLoading("advancesPositions", true);
      const response = await get(`/bankStatements/getAllAdvances/${dealApplicationId}`);
      const normalizedSource = (source || "").toLowerCase();

  
      if (Array.isArray(response)) {
        let filteredData = response;
  
        if (normalizedSource === "smartscrub") {
          const allowedSources = ["ocr", "plaid", "internal"];
          filteredData = filteredData.filter((row: any) =>
            allowedSources.includes((row.source || "").toLowerCase())
          );
        } else if (normalizedSource === "moneythumb") {
          filteredData = filteredData.filter(
            (row: any) => (row.source || "").toLowerCase() === "moneythumb"
          );
        }
  
        const dataWithSelection = filteredData.map((row) => ({
          ...row,
          selected: row.status === "Active",
        }));
        setAdvancesData(dataWithSelection);
      } else {
        setAdvancesData([]);
        console.warn("Unexpected advances response:", response);
      }
    } catch (error) {
      console.error("Error fetching advances:", error);
      setAdvancesData([]);
    } finally {
      setComponentLoading("advancesPositions", false);
    }
  };


  return (
    <div style={isReadOnly ? { pointerEvents: "none", opacity: 0.7 } : {}}>
      <SummaryCard  
        dealApplicationId={dealApplicationId}
        allowButton={true} permittedTabs={permittedTabs}
        allowSummarySelection={true}
        refreshDataBasedOnSource={reloadSourceData}
        allowResubmit={false} 
        onResubmit={undefined}
        />

      <Offers dealApplicationId={dealApplicationId} setComponentLoading={setComponentLoading} />

      {isRefi && (
        <DealPayOffs
          dealApplicationId={dealApplicationId}
          setComponentLoading={setComponentLoading}
        />
      )}
      <ExternalPayOffs
        dealApplicationId={dealApplicationId}
        setComponentLoading={setComponentLoading}
      />
      <EarlyPayOffs
        dealApplicationId={dealApplicationId}
        setComponentLoading={setComponentLoading}
      />
      <FeesHoldbacks
        dealApplicationId={dealApplicationId}
        setComponentLoading={setComponentLoading}
      />
      <Stipulations
        dealApplicationId={dealApplicationId}
        setComponentLoading={setComponentLoading}
      />
      <Commission dealApplicationId={dealApplicationId} setComponentLoading={setComponentLoading} />
      <Syndication
        dealApplicationId={dealApplicationId}
        setComponentLoading={setComponentLoading}
      />

      {isGlobalLoading && <FullPageLoader />}
    </div>
  );
};

export default Underwriting;
